import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { SmartTagInput } from '@/components/brand-bible';
import { normalizeWebsite } from '@/lib/domain';
import type { OnboardingDraft } from '../useOnboardingStore';
import {
  Eyebrow,
  FieldLabel,
  Heading,
  Lede,
  Note,
  PrimaryButton,
  QuietLink,
  SecondaryButton,
  StatusRow,
  StepBody,
  StepCounter,
  StepFooter,
} from '../OnboardingShell';

function SetupFooter({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  busy,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  busy?: boolean;
}) {
  return (
    <StepFooter>
      <PrimaryButton onClick={onNext} disabled={nextDisabled || busy}>
        {busy ? 'Working…' : nextLabel}
      </PrimaryButton>
      <SecondaryButton onClick={onBack} disabled={busy}>
        Back
      </SecondaryButton>
    </StepFooter>
  );
}

export function BusinessInfo({
  draft,
  onChange,
  onNext,
  onBack,
}: {
  draft: OnboardingDraft;
  onChange: (next: Partial<OnboardingDraft>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <StepBody>
        <div className="flex items-baseline justify-between gap-3">
          <Eyebrow>Your business</Eyebrow>
          <StepCounter current={1} total={4} />
        </div>
        <Heading>What's the name?</Heading>
        <Lede>This is how we label your business in the app. You can change it later.</Lede>
        <FieldLabel htmlFor="onboarding-name">Business name</FieldLabel>
        <Input
          id="onboarding-name"
          value={draft.businessName}
          onChange={(e) => onChange({ businessName: e.target.value })}
          placeholder="e.g. Rapid Flow Plumbing"
        />
      </StepBody>
      <SetupFooter
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue"
        nextDisabled={!draft.businessName.trim()}
      />
    </>
  );
}

export function ServiceArea({
  draft,
  onChange,
  onNext,
  onBack,
}: {
  draft: OnboardingDraft;
  onChange: (next: Partial<OnboardingDraft>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <StepBody>
        <div className="flex items-baseline justify-between gap-3">
          <Eyebrow>Where you work</Eyebrow>
          <StepCounter current={2} total={4} />
        </div>
        <Heading>Where do you take jobs?</Heading>
        <Lede>Cities, counties, or a service radius — however you describe it to customers.</Lede>
        <FieldLabel>Service area</FieldLabel>
        <SmartTagInput
          value={draft.locations}
          onChange={(locations) => onChange({ locations })}
          placeholder="Type a city or area and press Enter"
        />
      </StepBody>
      <SetupFooter
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue"
        nextDisabled={draft.locations.length === 0}
      />
    </>
  );
}

export function Website({
  draft,
  onChange,
  onNext,
  onBack,
}: {
  draft: OnboardingDraft;
  onChange: (next: Partial<OnboardingDraft>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const canonical = useMemo(() => normalizeWebsite(draft.domain), [draft.domain]);
  return (
    <>
      <StepBody>
        <div className="flex items-baseline justify-between gap-3">
          <Eyebrow>Your website</Eyebrow>
          <StepCounter current={3} total={4} />
        </div>
        <Heading>What's the site?</Heading>
        <Lede>
          Domain first. The plugin key is optional here — skip it if you don't have WordPress
          access yet.
        </Lede>
        <div className="space-y-4">
          <div>
            <FieldLabel htmlFor="onboarding-domain">Website</FieldLabel>
            <Input
              id="onboarding-domain"
              value={draft.domain}
              onChange={(e) => onChange({ domain: e.target.value })}
              placeholder="yourbusiness.com"
            />
            {canonical ? (
              <p className="mt-1.5">
                <Note>We'll store this as {canonical}</Note>
              </p>
            ) : null}
          </div>
          <div>
            <FieldLabel htmlFor="onboarding-plugin">Plugin key (optional)</FieldLabel>
            <Input
              id="onboarding-plugin"
              value={draft.pluginKey}
              onChange={(e) => onChange({ pluginKey: e.target.value, pluginSkipped: false })}
              placeholder="sk_agileco_…"
              autoComplete="off"
            />
            <p className="mt-2">
              <Note>
                Upload the zip in wp-admin — your contact sent it. Then paste the key from WP
                Settings → AgileCO Platform. We don't host a download here.
              </Note>
            </p>
          </div>
        </div>
        <div className="mt-5">
          <QuietLink onClick={onBack}>Back</QuietLink>
        </div>
      </StepBody>
      <StepFooter>
        <PrimaryButton onClick={onNext} disabled={!canonical}>
          Continue
        </PrimaryButton>
        <SecondaryButton
          onClick={() => {
            onChange({ pluginSkipped: true });
            onNext();
          }}
          disabled={!canonical}
        >
          Skip plugin for now
        </SecondaryButton>
      </StepFooter>
    </>
  );
}

export function Review({
  draft,
  busy,
  onConfirm,
  onBack,
}: {
  draft: OnboardingDraft;
  busy: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const canonical = normalizeWebsite(draft.domain);
  const pkgLabel =
    draft.package === 'both' ? 'SEO and Ads' : draft.package === 'ads' ? 'Ads' : 'SEO';
  return (
    <>
      <StepBody>
        <div className="flex items-baseline justify-between gap-3">
          <Eyebrow>Check this</Eyebrow>
          <StepCounter current={4} total={4} />
        </div>
        <Heading>Create {draft.businessName.trim() || 'your business'}?</Heading>
        <Lede>This is the last step before your business exists in the app.</Lede>
        <StatusRow tone="done" title="Package" detail={pkgLabel} />
        <StatusRow
          tone={draft.businessName.trim() ? 'done' : 'pending'}
          title="Name"
          detail={draft.businessName.trim() || 'Missing'}
        />
        <StatusRow
          tone={canonical ? 'done' : 'pending'}
          title="Website"
          detail={canonical || 'Missing'}
        />
        <StatusRow
          tone={draft.locations.length ? 'done' : 'pending'}
          title="Where you work"
          detail={draft.locations.join(', ') || 'Missing'}
        />
        <StatusRow
          tone={draft.pluginKey.trim() ? 'done' : 'pending'}
          title="Plugin key"
          detail={
            draft.pluginKey.trim()
              ? 'Pasted — we still have to check the site is active'
              : 'Skipped — prove the site later'
          }
        />
      </StepBody>
      <SetupFooter
        onBack={onBack}
        onNext={onConfirm}
        nextLabel="Create my business"
        nextDisabled={!draft.businessName.trim() || !canonical || draft.locations.length === 0}
        busy={busy}
      />
    </>
  );
}
